import { useEffect, useState } from 'react'

type UsercentricsUi = {
  isInitialized?: () => boolean
  isConsentRequired?: () => boolean
}

declare global {
  interface Window {
    UC_UI?: UsercentricsUi
  }
}

// Give up waiting for a Usercentrics script that never loads (blocked, slow).
const CMP_LOAD_TIMEOUT_MS = 5000
// Once initialised and consent is needed, the banner should open at once. If
// no layer appears within this time there is nothing to wait for.
const LAYER_GRACE_MS = 1500

/**
 * True once the Usercentrics banner is out of the way. The DS modal traps
 * focus and blocks clicks outside it, so opening it over the banner leaves the
 * guest unable to give consent. Not waiting forever: without Usercentrics the
 * hook resolves after a timeout.
 */
export const useConsentResolved = (): boolean => {
  const [resolved, setResolved] = useState(false)

  useEffect(() => {
    let done = false
    let layerShown = false
    let loadTimer: ReturnType<typeof setTimeout> | undefined
    let graceTimer: ReturnType<typeof setTimeout> | undefined

    const cleanup = () => {
      clearTimeout(loadTimer)
      clearTimeout(graceTimer)
      window.removeEventListener('UC_UI_INITIALIZED', onInitialized)
      window.removeEventListener('UC_UI_VIEW_CHANGED', onViewChanged)
    }

    const finish = () => {
      if (done) {
        return
      }
      done = true
      cleanup()
      setResolved(true)
    }

    function onViewChanged(event: Event) {
      const view = (event as CustomEvent<{ view?: string }>).detail?.view
      if (view === 'NONE') {
        if (layerShown) {
          finish()
        }
      } else {
        layerShown = true
      }
    }

    function onInitialized() {
      clearTimeout(loadTimer)
      if (window.UC_UI?.isConsentRequired?.() === false) {
        finish()
        return
      }
      graceTimer = setTimeout(() => {
        if (!layerShown) {
          finish()
        }
      }, LAYER_GRACE_MS)
    }

    window.addEventListener('UC_UI_VIEW_CHANGED', onViewChanged)
    if (window.UC_UI?.isInitialized?.()) {
      onInitialized()
    } else {
      window.addEventListener('UC_UI_INITIALIZED', onInitialized)
      loadTimer = setTimeout(finish, CMP_LOAD_TIMEOUT_MS)
    }

    return cleanup
  }, [])

  return resolved
}
